import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { apiGet } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import {
  getEntityTypeLabel,
  pluralizeEntityLabel,
} from '../utils/entityTypeUtils';

type RecentJobStatus =
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled';

interface RecentBulkActionJob {
  id: number;
  action_type: string;
  entity_type_name: string;
  status: RecentJobStatus;
  result_summary: {
    deleted_count?: number;
    failed_count?: number;
    error_categories?: Record<string, number>;
  } | null;
  completed_at: string | null;
}

interface RecentBulkActionJobsResponse {
  jobs: RecentBulkActionJob[];
}

function getPluralLabel(entityTypeName: string): string {
  const singular = getEntityTypeLabel(entityTypeName).toLowerCase();
  return pluralizeEntityLabel(singular);
}

function dispatchToastForJob(job: RecentBulkActionJob): void {
  const plural = getPluralLabel(job.entity_type_name);
  const deletedCount = job.result_summary?.deleted_count ?? 0;
  const failedCount = job.result_summary?.failed_count ?? 0;
  const totalAttempted = deletedCount + failedCount;

  if (job.status === 'completed') {
    toast.success(`${deletedCount} ${plural} deleted`, { duration: 5000 });
    return;
  }
  if (job.status === 'completed_with_errors') {
    toast.warning(
      `Deleted ${deletedCount} of ${totalAttempted} ${plural}. Some couldn't be deleted.`,
      { duration: 7000 }
    );
    return;
  }
  if (job.status === 'failed') {
    toast.error('Bulk delete failed', {
      description: 'Try again or contact support if this persists.',
      duration: 7000,
    });
    return;
  }
  if (job.status === 'cancelled') {
    toast('Bulk deletion cancelled.', { duration: 5000 });
    return;
  }
}

export function useBulkActionRecentToasts(orgId: number | null): void {
  const firedOrgsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (orgId === null) {
      return;
    }
    if (firedOrgsRef.current.has(orgId)) {
      return;
    }
    firedOrgsRef.current.add(orgId);

    let cancelled = false;
    apiGet<RecentBulkActionJobsResponse>(
      `/api/orgs/${orgId}/crm-events/bulk-action/jobs/recent/`
    )
      .then(response => {
        if (cancelled) {
          return;
        }
        const jobs = response?.jobs ?? [];
        for (const job of jobs) {
          dispatchToastForJob(job);
        }
      })
      .catch(error => {
        logger.warn('Failed to fetch recent bulk-action jobs', {
          component: 'crm_events',
          orgId,
          error: String(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [orgId]);
}
